import React, { useState, useEffect, useRef } from "react";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Image from "components/Elements/Image/Image.tsx";
import Icon from "components/Elements/Icon/Icon.tsx";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import EmbedLimitOptionsModal from "components/Reusable/JUMBOform/Modals/EmbedLimitOptionsModal.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";

const DOMAIN_VALIDATOR =
  /^(https?:\/\/)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+|localhost|((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?))(:\d+)?(\/)?$/;

const Security = ({
  protectFormWithPassword,
  setProtectFormWithPassword,
  passwordForm,
  setPasswordForm,
  setUpdateBeforeSave,
  emptyPassword,
  setEmptyPassword,
  allowAccess,
  setAllowAccess,
  displayToSubUsers,
  setDisplayToSubUsers,
  displayTypeSubUsers,
  setDisplayTypeSubUsers,
  listDomains,
  setListDomains,
  isCheckedPreventDisplayForm,
  setIsCheckedPreventDisplayForm,
  emptyDomainsError,
  setEmptyDomainsError,
}) => {
  const { getTextByKey, getHtmlByKey } = useContent();
  const { isPremium, isBusiness } = useUser();
  const { setModal } = useModal();
  const [tempFocusPassword, setTempFocusPassword] = useState(null);
  const [tempBlurPassword, setTempBlurPassword] = useState(null);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [oldProtectWithPassword, setOldProtectWithPassword] = useState(
    protectFormWithPassword
  );
  const [oldAllowAccess, setOldAllowAccess] = useState(allowAccess);

  // --- Custom Domains State ---

  const [singleDomain, setSingleDomain] = useState("");
  const [invalidDomain, setInvalidDomain] = useState(false);
  const [invalidDomainLimit, setInvalidDomainLimit] = useState(false);
  const domainsRef = useRef();

  const MAX_DOMAINS = 20;

  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (tempBlurPassword !== tempFocusPassword && protectFormWithPassword) {
      setUpdatePassword(true);
    }
  }, [tempBlurPassword]);

  const handleChangePassword = (e) => {
    setEmptyPassword(false);
    setPasswordForm(e);
  };

  const handleChangeProtectFormWithPassword = () => {
    if (isPremium()) {
      setProtectFormWithPassword((prev) => !prev);
      setPasswordForm("");
      setEmptyPassword(false);
    } else {
      setModal(() => <EmbedLimitOptionsModal />);
    }
  };

  const handleChangeAllowAccess = () => {
    if (isPremium()) {
      setAllowAccess((prev) => !prev);
      setUpdatePassword(true);
    } else {
      setModal(() => <EmbedLimitOptionsModal />);
    }
  };

  const handleChangeDisplaySubUsers = () => {
    if (isBusiness() || isPremium()) {
      setDisplayToSubUsers((prev) => !prev);
      setUpdatePassword(true);
    } else {
      setModal(() => <EmbedLimitOptionsModal />);
    }
  };

  const handleChangeDisplayTypeSubUsers = (e) => {
    setDisplayTypeSubUsers(e);
  };

  useEffect(() => {
    if (updatePassword) {
      setUpdateBeforeSave({ mode: true, action: "updatePassword" });
      setUpdatePassword(false);
      setOldProtectWithPassword(protectFormWithPassword);
      setOldAllowAccess(allowAccess);
    }
  }, [updatePassword]);

  useEffect(() => {
    if (!protectFormWithPassword && oldProtectWithPassword) {
      setUpdatePassword(true);
    }
  }, [protectFormWithPassword]);

  const DisplayToTypeOfSubUsers = {
    "display-to-all-sub-users": "All",
    "display-to-admin-and-supervisor": "AdminAndSupervisor",
    "display-to-admin": "Admin",
  };

  // --- Custom Domains Logic ---
  const handleChangeDomain = (value) => {
    setSingleDomain(value);
    setEmptyDomainsError(false);
    setInvalidDomain(false);
  };

  const handleRemoveDomain = (domain) => {
    const newList = listDomains.filter((item) => item !== domain);
    setListDomains(newList);
    if (newList.length >= 1) {
      setEmptyDomainsError(false);
    }
  };

  const updateDomainList = () => {
    setInvalidDomainLimit(false);
    const validDomain = new RegExp(DOMAIN_VALIDATOR);
    if (singleDomain) {
      const domains = singleDomain
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
      let hasInvalid = false;
      let newDomains = [...listDomains];
      if (newDomains.length + domains.length > MAX_DOMAINS) {
        setInvalidDomainLimit(true);
        return;
      }
      domains.forEach((domain) => {
        if (validDomain.test(domain)) {
          if (!newDomains.includes(domain)) {
            newDomains.push(domain);
          }
        } else {
          hasInvalid = true;
        }
      });
      setListDomains(newDomains);
      setInvalidDomain(hasInvalid);
      if (!hasInvalid) setSingleDomain("");
      if (!hasInvalid && newDomains.length > 0) {
        setEmptyDomainsError(false);
      }
    }
  };

  const handleEnterKeyDomain = (e) => {
    if (e.key === "Enter") {
      updateDomainList();
    }
  };

  useOnClickOutside(domainsRef, updateDomainList);

  return (
    <div className="security-container option">
      <div className="input-status-div">
        <Image
          alt=""
          title=""
          className="input-checkbox"
          onClick={(e) => {
            handleChangeProtectFormWithPassword();
          }}
          name={
            isPremium()
              ? protectFormWithPassword
                ? "checkbox-checked"
                : "checkbox-unchecked"
              : "checkbox-unchecked"
          }
          ext="svg"
        />
        <span className="span-text">
          {getTextByKey("protect-with-password-checkbox")}
        </span>{" "}
        <Tooltip
          id={"protect-form"}
          tooltipTextKey="protect-with-password-tooltip"
          //hardPositioned
          top
        >
          <Icon name="question-circle" iconClass="tooltip-question" />
        </Tooltip>
      </div>
      <div
        className={`password-input ${
          !protectFormWithPassword ? "hide-password-input" : ""
        }`}
      >
        <div className="text">{getTextByKey("set-password")}</div>
        <div
          className={`input ${!protectFormWithPassword ? "disabled" : ""} ${
            emptyPassword ? "empty-password" : ""
          }`}
        >
          <input
            onFocus={(e) => setTempFocusPassword(e.target.value)}
            onBlur={(e) => setTempBlurPassword(e.target.value)}
            disabled={!protectFormWithPassword}
            type="text"
            value={isPremium() ? passwordForm : ""}
            onChange={(e) => {
              handleChangePassword(e.target.value);
            }}
          />
        </div>
        {emptyPassword && (
          <div className="invalid-alert-text">
            {getTextByKey("empty-password")}
          </div>
        )}
      </div>
      <div className="allow-access-container">
        <div className="input-status-div">
          <Image
            alt=""
            title=""
            className="input-checkbox"
            onClick={(e) => {
              handleChangeAllowAccess();
            }}
            name={
              isPremium()
                ? allowAccess
                  ? "checkbox-checked"
                  : "checkbox-unchecked"
                : "checkbox-unchecked"
            }
            ext="svg"
          />
          <span className="span-text">
            {getTextByKey("create-form-allow-access-text")}
          </span>{" "}
          <Tooltip
            id={"allow-access-to-form"}
            tooltipTextKey="create-form-allow-access-tooltip"
            //hardPositioned
            top
          >
            <Icon name="question-circle" iconClass="tooltip-question" />
          </Tooltip>
        </div>
      </div>
      <div className="display-sub-users-container">
        <div className="input-status-div">
          <Image
            alt=""
            title=""
            className="input-checkbox"
            onClick={(e) => {
              handleChangeDisplaySubUsers();
            }}
            name={
              isBusiness() || isPremium()
                ? displayToSubUsers
                  ? "checkbox-checked"
                  : "checkbox-unchecked"
                : "checkbox-unchecked"
            }
            ext="svg"
          />
          <span className="span-text">
            {getTextByKey("create-form-display-to-sub-users-text")}
          </span>{" "}
          <Tooltip
            id={"display-to-sub-users"}
            tooltipTextKey="create-form-display-to-sub-users-tooltip"
            //hardPositioned
            top
          >
            <Icon name="question-circle" iconClass="tooltip-question" />
          </Tooltip>
        </div>

        {displayToSubUsers && (
          <div className="display-sub-users-type">
            <select
              value={displayTypeSubUsers}
              onChange={(e) => {
                handleChangeDisplayTypeSubUsers(e.target.value);
              }}
            >
              {Object.keys(DisplayToTypeOfSubUsers).map((item, i) => (
                <option value={DisplayToTypeOfSubUsers[item]} key={i}>
                  {getTextByKey(item)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="prevent-display-form-container">
        <div className="input-status-div">
          <Image
            alt=""
            title=""
            className="input-checkbox"
            onClick={() => {
              setIsCheckedPreventDisplayForm((prev) => {
                if (prev) {
                  console.log("!!!prev", prev);
                  setListDomains([]);
                }
                return !prev;
              });
              setEmptyDomainsError(false);
            }}
            name={
              isCheckedPreventDisplayForm
                ? "checkbox-checked"
                : "checkbox-unchecked"
            }
            ext="svg"
          />
          <span className="span-text">
            {getTextByKey("prevent-display-form-on-unlisted-domains")}
          </span>{" "}
          <Tooltip
            id={"display-to-sub-users"}
            tooltipTextKey="prevent-display-form-on-unlisted-domains-tooltip"
            //hardPositioned
            top
          >
            <Icon name="question-circle" iconClass="tooltip-question" />
          </Tooltip>
        </div>
        {isCheckedPreventDisplayForm && (
          <div
            className={`domain-field ${
              invalidDomain || invalidDomainLimit || emptyDomainsError
                ? "invalid-domain"
                : ""
            }`}
          >
            <div className="prevent-display-form-container-title">
              {getTextByKey("domain-list-text")}
            </div>
            <div className="recipients-div">
              {listDomains &&
                listDomains.map((item, i) => (
                  <div key={i} className="item-recipient">
                    <span>{item}</span>
                    <span
                      onClick={() => handleRemoveDomain(item)}
                      className="x-icon"
                    >
                      <Icon name="x" />
                    </span>
                  </div>
                ))}
            </div>
            <div className="div-input">
              <input
                ref={domainsRef}
                type="text"
                onChange={(e) => handleChangeDomain(e.target.value)}
                onKeyDown={handleEnterKeyDomain}
                value={singleDomain}
              />
            </div>
            {emptyDomainsError ? (
              <>
                <span className="invalid-domain-text span-text">
                  {getTextByKey("empty-domains-error")}
                </span>
                <br />
              </>
            ) : null}
            {invalidDomain ? (
              <>
                <span className="invalid-domain-text span-text">
                  {getTextByKey("invalid-domain-address")}
                </span>
                <br />
              </>
            ) : null}
            {invalidDomainLimit ? (
              <>
                <span className="invalid-domain-text span-text">
                  {getTextByKey("max-domains-limit")}
                </span>
                <br />
              </>
            ) : null}

            <div className="small-text domain-text">
              <span className="span-text">
                {getTextByKey("domain-address-text")}
              </span>
            </div>
            <div className="small-text">
              <span className="span-text">
                {getHtmlByKey("display-domain-by-condition-text")}
              </span>
            </div>
            <div className="domain-script-example">
              <div className="domain-script-flex">
                <textarea
                  readOnly
                  className="domain-script-textarea"
                  value={`<script>
window.addEventListener("message", (event) => {
  if (event.data?.type === "JM_EMBED_DOMAIN_CHECK") {
    event.source.postMessage(
      {
        type: "JM_EMBED_DOMAIN_CHECK_RESPONSE",
        domain: window.location.hostname,
      },
      event.origin
    );
  }
});
</script>`}
                  id="domain-script-textarea"
                />
                <div
                  className="copy-icon domain-script-copy"
                  onClick={async () => {
                    const textarea = document.getElementById(
                      "domain-script-textarea"
                    );
                    if (textarea) {
                      try {
                        await navigator.clipboard.writeText(textarea.value);
                        setIsCopied(true);
                        setTimeout(() => setIsCopied(false), 2000);
                      } catch (e) {
                        setIsCopied(false);
                      }
                    }
                  }}
                >
                  <Icon
                    title={getTextByKey("jumboforms-copy-code")}
                    name="copy-b"
                  />
                  {isCopied && (
                    <div>
                      <span className="domain-script-copied-text">
                        {getTextByKey("copied-text")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Security;
